import React, { useEffect, useState } from "react";
import { Table } from "react-bootstrap";
import { Link } from "react-router-dom";

import classNames from "classnames";

import { INotification } from "@src/types/api";

import { selectSetting } from "@features/global/globalSlice";

import { useAppSelector } from "@hooks/reduxHooks";
import useUserSwitcher from "@hooks/useUserSwitcher";

import { getNotifications } from "@lib/api";
import { TEAM_LOGO_URL } from "@lib/globals";
import { CheckIcon, DashIcon, QuestionIcon, XIcon } from "@lib/icons";
import { defaultDateFormat } from "@lib/utils";

import { emitSuccess } from "@services/toasts";

import { useIndexContext } from "@Clubhouse/components/Index/contexts/Index";
import useClubhouseNotifications from "@Clubhouse/components/Index/hooks/useClubhouseNotifications";
import useTableHandlers from "@Clubhouse/components/Index/hooks/useTableHandlers";

const AdminTable = (): React.JSX.Element => {
    const { sortColumn, sortOrder, teams, filters } = useIndexContext();
    const [notifications, setNotifications] = useState<Array<INotification>>(
        [],
    );

    const currentYear = parseInt(
        useAppSelector(state =>
            selectSetting(state, "majorybl_current_league_year"),
        )?.setting,
    );

    const { handleUserSwitch } = useUserSwitcher();
    const { handleHeaderClick } = useTableHandlers();
    const emitToast = (message: string) => emitSuccess(message);

    const {
        sendLogoNotification,
        sendRosterNotification,
        sendCoiNotification,
    } = useClubhouseNotifications(setNotifications, emitToast);

    useEffect(() => {
        let isMounted = true;
        void getNotifications("recent", null, "team")
            .then(response => {
                if (isMounted) {
                    setNotifications(response.notifications);
                }
            })
            .catch(() => {});
        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <Table
            data-testid="clubhouseTable"
            className="clubhouseTable"
            striped
            bordered
            responsive
        >
            <thead>
                <tr>
                    <th data-sortable="false">Logo</th>
                    <th
                        onClick={() => handleHeaderClick("team")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "team" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "team" &&
                                sortOrder === "descending",
                        })}
                    >
                        Team
                    </th>
                    <th
                        onClick={() => handleHeaderClick("age")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "age" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "age" &&
                                sortOrder === "descending",
                        })}
                    >
                        Age Level
                    </th>
                    <th
                        onClick={() => handleHeaderClick("firstName")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "firstName" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "firstName" &&
                                sortOrder === "descending",
                        })}
                    >
                        First Name
                    </th>
                    <th
                        onClick={() => handleHeaderClick("lastName")}
                        className={classNames({
                            sorting_asc:
                                sortColumn === "lastName" &&
                                sortOrder === "ascending",
                            sorting_desc:
                                sortColumn === "lastName" &&
                                sortOrder === "descending",
                        })}
                    >
                        Last Name
                    </th>
                    <th data-sortable="false">Phone</th>
                    <th data-sortable="false">Email</th>
                    <th>Roster</th>
                    <th>COI</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {teams
                    .filter(t =>
                        filters.search
                            ? t.name
                                  .toLowerCase()
                                  .includes(filters.search.toLowerCase()) ||
                              t.coach.email
                                  .toLowerCase()
                                  .includes(filters.search.toLowerCase()) ||
                              t.coach.firstName
                                  .toLowerCase()
                                  .includes(filters.search.toLowerCase()) ||
                              t.coach.lastName
                                  .toLowerCase()
                                  .includes(filters.search.toLowerCase()) ||
                              t.coach.phone
                                  .toLowerCase()
                                  .includes(filters.search.toLowerCase())
                            : true,
                    )
                    .filter(
                        t =>
                            filters.activeLeague ===
                            t.currentAgeLevel.season.season,
                    )
                    .filter(t =>
                        filters.ageLevels.filter && t.currentAgeLevel
                            ? filters.ageLevels.filter.includes(
                                  t.currentAgeLevel?.ageLevel,
                              )
                            : true,
                    )
                    .filter(t =>
                        filters.leagues.filter
                            ? t.leagues
                                ? t.leagues.some(
                                      l =>
                                          l.year === currentYear &&
                                          filters.leagues.filter?.includes(
                                              l.name,
                                          ),
                                  )
                                : false
                            : true,
                    )
                    .map(team => {
                        const currentTeamsLeague = team?.leagues?.find(
                            l => l.year === currentYear,
                        );

                        const lastLogoNotification = notifications.find(
                            n =>
                                n.notificationType === "logo" &&
                                n.id === team.id,
                        );
                        const lastRosterNotification = notifications.find(
                            n =>
                                n.notificationType === "roster" &&
                                n.id === team.id,
                        );
                        const lastCoiNotification = notifications.find(
                            n =>
                                n.notificationType === "coi" &&
                                n.id === team.id,
                        );

                        return (
                            <tr key={`${team.id}-${team.id}`}>
                                <td className="text-center">
                                    {team.logo ? (
                                        <img
                                            src={`${TEAM_LOGO_URL}${team.logo}`}
                                            alt={`${team.name} logo`}
                                            className="teamLogo img-fluid"
                                        />
                                    ) : (
                                        <>
                                            <XIcon
                                                className="icon text-danger text-xl cursor-pointer"
                                                onClick={sendLogoNotification(
                                                    team.id,
                                                )}
                                            />
                                            {lastLogoNotification && (
                                                <>
                                                    <br />
                                                    <small>
                                                        Last notification:
                                                        <br />{" "}
                                                        {defaultDateFormat.format(
                                                            new Date(
                                                                lastLogoNotification.sentAt,
                                                            ),
                                                        )}
                                                    </small>
                                                </>
                                            )}
                                        </>
                                    )}
                                </td>
                                <td>
                                    <Link
                                        to={`/coachs-clubhouse/team/${team.id}`}
                                    >
                                        <h5 className="teamLink">
                                            {team.name}
                                        </h5>
                                    </Link>
                                    {currentTeamsLeague?.name ||
                                        team.currentAgeLevel?.ageLevel}
                                </td>
                                <td>{team.currentAgeLevel?.ageLevel}</td>
                                <td>{team.coach.firstName}</td>
                                <td>{team.coach.lastName}</td>
                                <td>
                                    <a href={`mailto:${team.coach.email}`}>
                                        {team.coach.email}
                                    </a>
                                </td>
                                <td>{team.coach.phone}</td>
                                <td className="text-center">
                                    {team.activeRosterCount >= 9 &&
                                    team.acceptedWaiverCount >= 9 ? (
                                        <CheckIcon className="icon text-success text-xl" />
                                    ) : (
                                        <>
                                            <XIcon
                                                className="icon text-danger text-xl cursor-pointer"
                                                onClick={sendRosterNotification(
                                                    team.id,
                                                )}
                                            />
                                            {lastRosterNotification && (
                                                <>
                                                    <br />
                                                    <small>
                                                        Last notification:
                                                        <br />{" "}
                                                        {defaultDateFormat.format(
                                                            new Date(
                                                                lastRosterNotification.sentAt,
                                                            ),
                                                        )}
                                                    </small>
                                                </>
                                            )}
                                        </>
                                    )}
                                </td>
                                <td className="text-center">
                                    {team.coiYear ? (
                                        team.coiYear === currentYear &&
                                        team.coiStatus === "Approved" ? (
                                            <CheckIcon className="icon text-success text-xl" />
                                        ) : team.coiStatus === "Rejected" ? (
                                            <XIcon className="icon text-danger text-xl" />
                                        ) : (
                                            <QuestionIcon className="icon text-warning text-xl" />
                                        )
                                    ) : (
                                        <DashIcon
                                            className="icon text-secondary text-xl cursor-pointer"
                                            onClick={sendCoiNotification(
                                                team.id,
                                            )}
                                        />
                                    )}
                                    {lastCoiNotification && (
                                        <>
                                            <br />
                                            <small>
                                                Last notification:
                                                <br />{" "}
                                                {defaultDateFormat.format(
                                                    new Date(
                                                        lastCoiNotification.sentAt,
                                                    ),
                                                )}
                                            </small>
                                        </>
                                    )}
                                </td>
                                <td>
                                    <span
                                        className="switchUserLink link"
                                        onClick={() =>
                                            handleUserSwitch(team.coach.id)
                                        }
                                    >
                                        switch to user
                                    </span>
                                </td>
                            </tr>
                        );
                    })}
            </tbody>
        </Table>
    );
};

export default AdminTable;
